import React, { useRef, useState } from 'react';
import { adminApi, formatApiError } from './adminApi';
import { Upload, AlertCircle, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/uploads/')) return `${BACKEND_URL}${url}`;
  return url;
};

/**
 * ImageUploader — drop-in image picker that accepts a URL OR uploaded file.
 * The parent owns the URL string in `value`; `onChange(newUrl)` is called when
 * the user types a URL or finishes uploading a file.
 */
const ImageUploader = ({ value, onChange, label = 'Cover image', testId = 'image-uploader' }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setSuccess(false);
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      onChange(res.url);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="/generated/blog_performance.png or paste URL"
          className="flex-1 px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white font-mono text-xs focus:outline-none focus:border-[#83BF42] transition-colors"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-pick`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/10 text-white/80 text-xs font-['Inter'] hover:bg-white/5 transition disabled:opacity-50 whitespace-nowrap"
        >
          {uploading ? (
            <span className="w-3 h-3 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
          ) : success ? (
            <Check className="w-3.5 h-3.5 text-[#83BF42]" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />

      {error && (
        <p className="text-red-400 text-xs flex items-center gap-1.5 mt-1.5">
          <AlertCircle className="w-3 h-3" /> {error}
        </p>
      )}

      {value && (
        <div className="mt-2 rounded-lg overflow-hidden border border-white/10 aspect-video bg-black/30">
          <img
            src={resolveUrl(value)}
            alt={label}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.opacity = '0.3';
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
